import { ImageName } from "@mcleod/images";
import {
    Api, Currency, CurrencySettings, CurrencyUtil, getThemeColor, HorizontalAlignment, ModelRow, StringUtil
} from "@mcleod/core";
import {
    AddType, BlurEvent, ChangeEvent, Component, DataDisplayEvent, DataSourceAction, DataSourceExecutionEvent,
    DataSourceMode, DataSourceModeChangeEvent, DropdownItem, ForcedCase, Image, Label, Layout, Panel, Tab,
    TableContentsChangedEvent, TableRow, TableRowCreationEvent, TableRowMode, Textbox, TableRowDisplayEvent
} from "@mcleod/components";
import { ExchangeRateQuickInfo, getDispatchControlBoolean, LMEUserSettings } from "@mcleod/general";
import { MovementCarrierPay } from "./MovementCarrierPay";
import { AutogenLayoutMovementPay } from "./autogen/AutogenLayoutMovementPay";
import { CommonTooltips } from "@mcleod/common";

const MULTICURRENCY_FIELDS_FLAT = [
    "carrier_pay",
    "margin",
    "net_pay",
    "other_pay",
    "pay",
    "total_advances",
    "total_pay"
];

export interface PayError {
    message: string,
    details: string
}

export class MovementPay extends AutogenLayoutMovementPay {

    private _listenersActive = true;
    private _payError: PayError;
    private _fromSave: boolean = false;
    private _payAdjustmentsCurrency: Currency;
    private _totalPayCurrency: Currency;
    private _marginCurrency: Currency;
    private _carrierId: string;
    public carrierPayCallback: (errorMessage: string, errorDetails: string) => string;
    public cachedPayAdjustments: ModelRow[];

    get carrierId(): string {
        return this._carrierId;
    }

    set carrierId(value: string) {
        this._carrierId = value;
    }



    private _marginColor = (num) => {
        if (num > 0) {
            return getThemeColor("success");
        } else if (num <= 0) {
            return getThemeColor("error.lightest");
        } else {
            return getThemeColor(this.textboxMargin.color);
        }
    }

    override onLoad() {
        this.textboxOverrideType.forcedCase = ForcedCase.NONE;
        this.textboxMargin.currencyColorCallback = this._marginColor;
        this.textboxCurrencyCodeSelector.visible = CurrencySettings.getSingleton().multiCurrencyEnabled();
        this.layoutMoveCarrierPay.layoutTargetPay.addLayoutLoadListener(() => {
            this.layoutMoveCarrierPay.layoutTargetPay.textboxTargetPayCalc.addBlurListener(event => this.calculatePay(true));
        })
        this.layoutMoveCarrierPay.textboxMaxPayCalc.addBlurListener(event => this.calculatePay(true));
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            this.setupCarrierPayQuickInfo();
            this.setupTotalPanelCarrierPayQuickInfo();
            this.setupTotalPanelPayAdjustmentsQuickInfo();
            this.setupTotalPanelTotalPayQuickInfo();
            this.setupTotalPanelMarginQuickInfo();
        }
    }


    private get listenersActive() {
        return this._listenersActive;
    }

    private set listenersActive(value) {
        this._listenersActive = value ? value : !this._listenersActive;
    }

    public get payError(): PayError {
        return this._payError;
    }

    private set payError(value: PayError) {
        if (value?.message == null)
            value = null;
        if (value != this.payError) {
            this._payError = value;
        }
    }

    private get layoutMoveCarrierPay(): MovementCarrierPay {
        return this.layoutCarrierRates as MovementCarrierPay;
    }


    sourceMovementAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH && this.mainDataSource.activeRow != null) {
            this.setupExtraPayTab();
            const movementId = this.activeRow.get("id");
            if (movementId != null) {
                this.sourceExtraPay.search({ "movement_id": movementId }).then(() => {
                    this.cachedPayAdjustments = this.sourceExtraPay.data;
                });
                const showBtm = getDispatchControlBoolean('show_btm');
                const brokerage = this.activeRow.getBoolean('brokerage', false);
                const btmExclude = this.activeRow.getBoolean('buy_to_market_ex', false);
                const showBtmFields = showBtm && brokerage && !btmExclude;
                this.textboxBuyToMarket.visible = showBtmFields;
                this.textboxBuyToMarketPercent.visible = showBtmFields;

                if (this.mainDataSource?.activeRow?.get("brokerage") != "Y") {
                    this.textboxOverrideType.items = [new DropdownItem("D", "Distance"), new DropdownItem("F", "Flat rate")];
                }

                const transferredToSettlement: boolean = !this.sourceMovement.activeRow.isNull("xfer2settle_date");
                if (transferredToSettlement) {
                    this.tableExtraPay.enabled = false;
                    this.tableExtraPay.allowEdit = false;
                    this.tableExtraPay.addType = AddType.NOT_ALLOWED;
                    const warningImage = new Image({
                        name: ImageName.triangleWarning,
                        color: "warning",
                        marginRight: "auto",
                        marginLeft: 8,
                        rowBreak: false,
                        tooltip: "You cannot change Pay Adjustments after the movement has been transferred to Settlement."
                    });
                    this.tabPayAdjustment["countLabel"].fillRow = false;
                    this.tabPayAdjustment.heading.insert(warningImage, 2);
                }
            }
        }
        if (this.tableExtraPay.quickAddRow != null) {
            this.populateExtraPayDefaults(this.tableExtraPay.quickAddRow);
        }
    }

    setupExtraPayTab() {
        this.styleTab(this.tableExtraPay.parent as Tab);
        this.sourceExtraPay.addAfterExecutionListener(event => {
            this.updatePayAdjustmentsTabCaption();
        });
        this.tableExtraPay.addContentsChangedListener(event => {
            this.updatePayAdjustmentsTabCaption();
        });
    }

    sourceMovementAfterModeChange(event: DataSourceModeChangeEvent) {
        this.sourceExtraPay.mode = event.newMode;
        if (DataSourceMode.UPDATE == event.newMode)
            this.defaultPayUnitsAndMethod();
        if (event.newMode === DataSourceMode.ADD || event.newMode === DataSourceMode.UPDATE) {
            CurrencyUtil.configureCurrencySelector(this.textboxCurrencyCodeSelector);
        }
    }

    defaultPayUnitsAndMethod() {
        if (this.mainDataSource.activeRow.get("override_units") == null) {
            this.mainDataSource.activeRow.set("override_units", 1);
            this.textboxOverrideUnits.displayData(this.mainDataSource.activeRow, null, 0);
        }
        if (this.mainDataSource.activeRow.get("override_type") == null)
            this.mainDataSource.activeRow.set("override_type", "F");
    }

    updatePayAdjustmentsTabCaption() {
        const tab = this.tableExtraPay.parent as Tab;
        const count = this.tableExtraPay.rowCount;
        if (tab != null && tab["countLabel"] != null)
            tab["countLabel"].text = count + "";
    }

    async rateRelatedFieldChanged(event: BlurEvent | ChangeEvent) {
        if (this.listenersActive === true && ((event instanceof ChangeEvent && event.oldValue != event.newValue && event.userInitiatedChange == true) ||
            (event instanceof BlurEvent && event.changedWhileFocused))) {
            await this.calculatePay(true);
            if (event instanceof ChangeEvent) {
                this.textboxOverridePayRate.focus();
                this.textboxOverridePayRate.selectText();
            }
            this.labelAutoRated.visible = false;
            this.mainDataSource.activeRow?.set("carrier_rate_id", null);
            this.mainDataSource.activeRow?.set("carrier_lane_rate_id", null);
        }
    }

    payAdjustmentsChanged(): boolean {
        return this.tableExtraPay.dataSource.hasChanged();
    }

    async deleteFuelSurchargeRows() {
        for (let i = 0; i < this.sourceExtraPay.data.length; i++) {
            const row = this.sourceExtraPay.data[i] as ModelRow;
            if (row?.data["fuel_surcharge_pay"]) {
                await row.delete();
            }
        }
    }

    async calculatePay(editedByUser: boolean, autoRate: boolean = false, fromOffer: boolean = false, calculateFuelSurcharge: boolean = false,
                       validatePay: boolean = true): Promise<any> {
        this.labelAutoRated.visible = false;
        const payMethod = this.textboxOverrideType.selectedItem?.value;
        const units = this.textboxOverrideUnits.valueAsString;
        const payRate = this.textboxOverridePayRate.valueAsString;

        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            const orderCurrency = this.sourceMovement.activeRow?.get("orders.freight_charge_c");
            const selectedCurrency = this.sourceMovement.activeRow?.get("currency_selected", orderCurrency);
            const currencyTo = this.sourceMovement.activeRow?.get("override_pay_amt_c", selectedCurrency);
            if (this.mainDataSource.activeRow?.get("currency_selected") == null) {
                this.mainDataSource.activeRow?.set("currency_selected", currencyTo);
                this.mainDataSource.activeRow?.setLookupModelData("currency_selected", { currency_type_id: currencyTo });
                this.mainDataSource.activeRow.resetOriginalData("currency_selected");
                this.mainDataSource.displayDataInBoundComponent(this.textboxCurrencyCodeSelector);
            }
        }

        if (calculateFuelSurcharge) {
            await this.deleteFuelSurchargeRows();
        }

        return new Promise((resolve) => {
            Api.search("lme/dispatch/calculate-override-pay", {
                "validate_pay": validatePay,
                "movement_id": this.mainDataSource.activeRow.get("id"),
                "order_id": this.mainDataSource.activeRow?.get("orders.id"),
                "type": payMethod,
                "units": units,
                "rate": payRate || "0",
                "max_pay": this.mainDataSource.activeRow.get(this.layoutMoveCarrierPay.textboxMaxPayCalc.field),
                "target_pay": this.mainDataSource.activeRow.get(this.layoutMoveCarrierPay.layoutTargetPay.textboxTargetPayCalc.field),
                "changed_other_pay_rows": this.payAdjustmentsChanged() ? this.sourceExtraPay.data : null,
                "auto_rate": autoRate,
                "payee_id": this.mainDataSource.activeRow.get("pending_payee_id"),
                "calculate_fuel_surcharge": calculateFuelSurcharge,
                "move_currency_code": this.sourceMovement.activeRow?.get("currency_selected", CurrencySettings.getFunctionalCurrency()),
                "order_currency_code": this.sourceMovement.activeRow?.get("orders.freight_charge_c", CurrencySettings.getFunctionalCurrency())
            }).then(response => {
                this.displayCalculatedData(editedByUser, response, calculateFuelSurcharge, fromOffer);
                this.validateCarrierPayResult(response.data[0]);
                this.listenersActive = true;
                resolve(this.payError == null);
            });
        })
    }


    private validateCarrierPayResult(data: any) {
        const payValidation = data["validation_data"];
        const errorMessage = payValidation?.["dispatch_error_msg"];
        let errorDetails = payValidation?.["dispatch_update_pay_msg"];
        if (this.carrierPayCallback != null)
            errorDetails = this.carrierPayCallback(errorMessage, errorDetails);

        this.payError = { message: errorMessage, details: errorDetails };
    }

    private async displayCalculatedData(editedByUser: boolean, response: any, calculateFuelSurcharge: boolean, fromOffer: boolean) {
        const data = response.data[0];

        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            const currencyTo = this.sourceMovement.activeRow?.get("currency_selected");
            this.convertFlat(data, currencyTo, MULTICURRENCY_FIELDS_FLAT);
        }

        this.mainDataSource.activeRow?.set("override_type", data["type"] ?? this.activeRow.get("override_type"));
        if (!StringUtil.isEmptyString(data["type"])) {
            const items = this.textboxOverrideType.items as DropdownItem[];
            for (let i = 0; i < items.length; ++i) {
                if (items[i].value == data["type"]) {
                    this.textboxOverrideType.selectedItem = items[i];
                    break;
                }
            }
        }
        this.mainDataSource.activeRow?.set("override_units", data["units"] ?? this.activeRow.get("override_units"));
        if (!fromOffer && !StringUtil.isEmptyString(data["carrier_contact"])) {
            const contactLookupData = new ModelRow("lme/general/contact", false, data["_lookup_carrier_contact"]);
            this.mainDataSource.activeRow?.setLookupModelData("carrier_contact", contactLookupData);
            this.mainDataSource.activeRow?.set("carrier_email", data["carrier_email"]);
            this.mainDataSource.activeRow?.set("carrier_phone", data["carrier_phone"]);
        }
        if (editedByUser || !StringUtil.isEmptyString(data["pay"])) {
            if (!data["pay"]) {
                this.mainDataSource.activeRow?.set("override_pay_amt", null);
            } else {
                const payAmount = data["pay"] as Currency;
                this.mainDataSource.activeRow?.set("override_pay_amt", payAmount);
                const move_distance = this.mainDataSource.activeRow?.get("move_distance", 0);
                if (move_distance > 0) {
                    const payPerMileAmount = payAmount.amount / move_distance;
                    this.textboxPayPerMile.text = CurrencyUtil.formatCurrency(new Currency({
                        amount: payPerMileAmount,
                        currency_code: payAmount.currency_code,
                        conversion_date: payAmount.conversion_date
                    })) + "/mile";
                } else {
                    this.textboxPayPerMile.text = "";
                }
            }
        }
        this.textboxPayAdjustments.text = CurrencyUtil.formatCurrency(data.other_pay);
        this.textboxTotalPay.text = CurrencyUtil.formatCurrency(data.total_pay);
        this.textboxMargin.text = CurrencyUtil.formatCurrency(data.margin);
        if (!StringUtil.isEmptyString(data["buy_to_market"])) {
            const isOverMarket: boolean = data["buy_to_market"].includes("OVER");
            this.textboxBuyToMarketPercent.color = this.textboxBuyToMarket.color = isOverMarket ? "error" : data["buy_to_market_percent"] ? "success" : "default";
            this.textboxBuyToMarket.text = data.buy_to_market;
            this.textboxBuyToMarketPercent.text = data.buy_to_market_percent;
        }
        this._payAdjustmentsCurrency = data.other_pay;
        this._totalPayCurrency = data.total_pay;
        this._marginCurrency = data.margin;
        this.textboxMarginPercentage.text = data["margin_percentage"] ? `${data["margin_percentage"]}` : "--";
        if (this.textboxMarginPercentage.printableLabel)
            this.textboxMarginPercentage.printableLabel._element.style.color = this._marginColor(data["margin_percentage"]);
        this.mainDataSource.activeRow?.set("override_pay_rate", data["rate"] ?? this.activeRow.get("override_pay_rate"));
        if (data["carrier_rate_id"] != null) {
            this.mainDataSource.activeRow?.set("carrier_rate_id", data["carrier_rate_id"]);
            this.mainDataSource.activeRow?.set("carrier_lane_rate_id", data["carrier_rate_lane_id"]);
            this.labelAutoRated.visible = true;
            if (data["carrier_rate_lane_id"] != null) {
                CommonTooltips.setTooltipContentLayout(
                    this.labelAutoRated,
                    data["carrier_rate_lane_id"],
                    "lme/dispatch/rates/CarrierLaneRateQuickInfo"
                );
            }
        }
        if (calculateFuelSurcharge) {
            await this.sourceExtraPay.search({ "movement_id": this.mainDataSource.activeRow?.get("id") });
        }
    }

    private convertFlat(data, currencyTo: string, fieldList: string[]) {
        fieldList.forEach(field => {
            const originalCurrency = (data instanceof ModelRow) ? data.get(field) as Currency : data[field] as Currency;
            if (originalCurrency) {
                const result = new Currency(originalCurrency.amount);
                result.conversion_date = originalCurrency.conversion_date;
                result.setCurrencyCode(currencyTo);
                if (data instanceof ModelRow) {
                    data.set(field, result);
                } else {
                    data[field] = result;
                }
            }
        });
    }

    textboxDeductCodeIdBeforeLookupModelSearch(event) {
        event.filter.dispatcher_flag = 'Y';
    }

    textboxDeductCodeIdOnChange(event: ChangeEvent) {
        const textbox = event.target as Textbox;
        if (event.userInitiatedChange && event.newValue != event.oldValue) {
            const tableRow = TableRow.getContainingTableRow(textbox);
            const modelRow = tableRow.data as ModelRow;
            const textboxShortDesc = tableRow.findComponentById("textboxShortDesc") as Textbox;
            const selectingFromDropdown = textbox.isDropdownVisible() && textbox.getFirstLookupModelData() != null;
            if (selectingFromDropdown) {
                // after selecting a deduct code, set the textboxShortDesc.text and then textboxUnits gets focus
                modelRow.set("short_desc", textbox.getFirstLookupModelData().get("short_desc"));
                textbox.hideDropdown(false);
                this.payAdjustmentsTextboxChanged(textbox);
                tableRow.findComponentById("textboxUnits").focus();
            } else {
                modelRow.set("short_desc", null);
                textboxShortDesc.text = null;
            }
        }
    }

    textboxUnitsOnBlur(event: BlurEvent) {
        this.payAdjustmentsTextboxOnBlur(event);
    }

    textboxRateOnBlur(event: BlurEvent) {
        this.payAdjustmentsTextboxOnBlur(event);
    }

        textboxCurrencyOnDataDisplay(event: DataDisplayEvent) {
        const textbox = event.target as Textbox;
        const row = TableRow.getContainingTableRow(textbox).data as ModelRow;
        let currencyType = row.get("amount_c") as string;

        if (StringUtil.isEmptyString(currencyType)) {
            const amount = row.getCurrency("amount") || row.get("amount");
            currencyType = amount?.currency_code
                || row.get("payee_currency_type")
                || this.sourceMovement.activeRow?.get("currency_selected", CurrencySettings.getTransactionalCurrency())
                || CurrencySettings.getTransactionalCurrency();

            if (!StringUtil.isEmptyString(currencyType)) {
                row.set("amount_c", currencyType);
            }
        }

        if (!StringUtil.isEmptyString(currencyType)) {
            row.setLookupModelData("amount_c", { currency_type_id: currencyType });
            row.resetOriginalData("amount_c");
            textbox.text = currencyType;
        }
    }

    textboxCurrencyOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target);
        const modelRow = tableRow.data as ModelRow;
        if (event.hasSelection()) {
            this.applyPayAdjustmentCurrency(modelRow, event.selection.get("currency_type_id"));
        }
        tableRow.displayComponentValues();
    }

    textboxPayAdjustmentsPayeeIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const textbox = event.target as Textbox;
        if (CurrencySettings.getSingleton().multiCurrencyEnabled() && event.hasSelection()) {
            const tableRow = TableRow.getContainingTableRow(textbox);
            const modelRow = tableRow.data as ModelRow;
            this.setPayAdjustmentCurrencyFromPayee(modelRow, tableRow, event.selection.get("id"), event.selection);
        }
    }

    payAdjustmentsTextboxOnBlur(event: BlurEvent) {
        if (event.changedWhileFocused) {
            const textbox: Textbox = event.target as Textbox;
            this.payAdjustmentsTextboxChanged(textbox);
        }
    }

    payAdjustmentsTextboxChanged(textbox: Textbox) {
        const tableRow: TableRow = TableRow.getContainingTableRow(textbox);
        const rate = tableRow.data.get("rate");
        const units = tableRow.data.get("units");
        const rowPayeeId = tableRow.data.get("payee_id");
        let amountCurrency: string = tableRow.data.get("amount_c");
        const transactionalCurrency = CurrencySettings.getTransactionalCurrency();
        // get currency from order of movement if amount currency is not set, otherwise use transactional currency as fallback to avoid null currency
        const orderCurrency = this.sourceMovement.activeRow?.get("orders.freight_charge_c");
        const movementCurrency = this.sourceMovement.activeRow?.get("currency_selected", orderCurrency) || transactionalCurrency;
        if (amountCurrency == null) {
            amountCurrency = this.sourceExtraPay.activeRow?.get("currency_selected", movementCurrency) || movementCurrency;
        }
        tableRow.data.updateCurrency("amount", new Currency({
            amount: rate * units,
            currency_code: amountCurrency,
            conversion_date: this.sourceMovement.activeRow?.get("orders.freight_charge_d", new Date())
        }));
        if (!StringUtil.isEmptyString(textbox.text) && !StringUtil.isEmptyString(rowPayeeId)) {
            Api.search("lme/dispatch/calculate-carrier-other-pay",
                {
                    "other_pay_row": TableRow.getContainingTableRow(textbox).data,
                    "currency_code": amountCurrency
                }).then(response => {
                this.payAdjustmentsFromApi(response, textbox);
            });
        } else {
            const descTextbox: Textbox = tableRow.findComponentById("textboxShortDesc") as Textbox;
            tableRow.data.set("short_desc", "");
            descTextbox.displayData(tableRow.data, null, 0);
        }
    }

    private payAdjustmentsFromApi(response, textbox: Textbox) {
        const data = response.data[0];
        const payAdjustments = data["other_pay_row"];
        const row = TableRow.getContainingTableRow(textbox);
        row.data.set("transaction_date", payAdjustments["transaction_date"]);

        const payeeIdTextbox: Textbox = row.findComponentById("textboxPayAdjustmentsPayeeId") as Textbox;
        const payeeId: string = payAdjustments["payee_id"];
        row.data.set("payee_id", payeeId);
        row.data.setLookupModelData("payee_id", payAdjustments["_lookup_payee_id"]);
        payeeIdTextbox.displayData(row.data, null, 0);

        const descTextbox: Textbox = row.findComponentById("textboxShortDesc") as Textbox;
        if (payAdjustments["short_desc"] != null)
            descTextbox.text = payAdjustments["short_desc"];

        const units: Textbox = row.findComponentById("textboxUnits") as Textbox;
        units.text = payAdjustments["units"]?.toString();
        const rate: Textbox = row.findComponentById("textboxRate") as Textbox;
        rate.text = payAdjustments["rate"]?.toString();
        const amountValue = payAdjustments["amount"] as Currency;
        TableRow.getContainingTableRow(textbox).data.set("amount", amountValue);

        const chargeRow: TableRow = TableRow.getContainingTableRow(textbox);

        chargeRow.data.set("amount", amountValue);
        const amount: Textbox = row.findComponentById("textboxAmount") as Textbox;
        if (row.mode === TableRowMode.QUICK_ADD)
            amount.text = CurrencyUtil.formatCurrency(amountValue)?.toString();
    }

    createPayErrorPanel(): Panel {
        if (this.payError != null) {
            return new Panel({
                width: 450, rowBreak: false, padding: 0,
                components: [
                    new Label({
                        text: this.payError.message,
                        align: HorizontalAlignment.CENTER,
                        fontBold: true,
                        fontSize: "medium"
                    }),
                    new Label({ text: this.payError.details || "" })
                ]
            });
        }
    }

    styleTab(tab: Tab) {
        if (tab.heading != null && tab.headingLabel != null) {
            tab.heading.color = "subtle.light";
            tab.heading.setProps({ color: "subtle.light", fontBold: false, borderWidth: 0 });
            tab.headingLabel.setProps({ color: "default", fontBold: true, fillRow: false });
            if (tab["countLabel"] == null) {
                tab["countLabel"] = new Label({
                    fontBold: true, text: "0", fillRow: true,
                    color: "primary", rowBreak: false, marginLeft: 5
                });
                tab.heading.insert(tab["countLabel"], 1);
            }
        }
    }

    /** This is an event handler for the afterTabSelection event of tabsetPayAdjustments.  */
    tabsetPayAdjustmentsAfterTabSelection(event) {
        const tabHeader = this?.tabPayAdjustment?.["_heading"];
        if (tabHeader != null) {
            tabHeader.color = "subtle.light";
            tabHeader.backgroundColor = "table.headingRowBackgroundColor";
            this.tabPayAdjustment.headingLabel.color = "default";
        }
    }

    /** This is an event handler for the onContentsChanged event of tableExtraPay.  */
    tableExtraPayOnContentsChanged(event: TableContentsChangedEvent) {
        this.calculatePay(false);
    }

    private populateExtraPayDefaults(tableRow: TableRow) {
        const modelRow: ModelRow = tableRow.data;
        if (tableRow.mode === TableRowMode.QUICK_ADD && this.activeRow) {
            modelRow.set("order_id", this.activeRow.get("orders.id"));
            modelRow.set("movement_id", this.activeRow.get("id"));
            modelRow.set("entered_user_id", LMEUserSettings.getUserId());
            const amount = modelRow.getCurrency("amount", null);
            if (amount) {
                amount.setConversionDate(this.activeRow.get("orders.freight_charge_d", new Date()));
                modelRow.updateCurrency("amount", amount);
            }
            modelRow.setValueAndLookupModelData("payee_id", this.carrierId, { id: this.carrierId });
            this.applyPayAdjustmentCurrency(modelRow, this.activeRow.get("override_pay_amt_c", this.activeRow.get("currency_selected")));
        }
        else {
            const currencyType = modelRow.get("amount_c");
            modelRow.setValueAndLookupModelData("amount_c", currencyType, { currency_type_id: currencyType });
        }
        tableRow.displayComponentValues();
    }

    private applyPayAdjustmentCurrency(modelRow: ModelRow, currencyType: string) {
        modelRow.setValueAndLookupModelData("amount_c", currencyType, { currency_type_id: currencyType });

        const amount = modelRow.getCurrency("amount");
        if (amount) {
            amount.setConversionDate(this.sourceMovement.activeRow?.get("orders.freight_charge_d", new Date()));
            amount.setCurrencyCode(currencyType);
            modelRow.updateCurrency("amount", amount);
        }
    }

    private setPayAdjustmentCurrencyFromPayee(modelRow: ModelRow, tableRow: TableRow, payeeId?: string, payeeLookupData?: ModelRow) {
        if (!CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            return;
        }

        // if the payee is the same as the payee for the movement override pay, use the movement override pay currency
        const movementOverridePayPayeeId = this.activeRow?.get("override_payee_id", this.activeRow?.get("pending_payee_id"));
        let currencyType: string = this.activeRow.get("currency_selected");
        if (payeeId && movementOverridePayPayeeId && payeeId === movementOverridePayPayeeId) {
                this.applyPayAdjustmentCurrency(modelRow, currencyType);
        }
        else if (payeeLookupData) {
            currencyType = payeeLookupData.get("currency", currencyType);
            this.applyPayAdjustmentCurrency(modelRow, currencyType);
        }
        tableRow.displayComponentValues();
    }

    textboxCurrencyCodeSelectorOnLookupModelSelection() {
        this.calculatePay(true, true, false, true, true);
    }

    private setupCarrierPayQuickInfo() {
        this.textboxOverridePayAmt.tooltipDynamicContent = async () => {
            const amount: Currency = this.mainDataSource.activeRow.getCurrency("override_pay_amt");
            if (amount) {
                const layout = await Layout.getLoadedLayout("lme/general/multicurrency/ExchangeRateQuickInfo") as ExchangeRateQuickInfo;
                await layout.sourceCurrencyConversions?.search({ currency_for_exchange: amount });
                return layout;
            }
            return null;
        };
    }

    private setupTotalPanelCarrierPayQuickInfo() {
        this.textboxCarrierPay.tooltipDynamicContent = async () => {
            const amount: Currency = this.mainDataSource.activeRow.getCurrency("override_pay_amt");
            if (amount) {
                const layout = await Layout.getLoadedLayout("lme/general/multicurrency/ExchangeRateQuickInfo") as ExchangeRateQuickInfo;
                await layout.sourceCurrencyConversions?.search({ currency_for_exchange: amount });
                return layout;
            }
            return null;
        };
    }

    private setupTotalPanelPayAdjustmentsQuickInfo() {
        this.textboxPayAdjustments.tooltipDynamicContent = async () => {
            const amount: Currency = this._payAdjustmentsCurrency;
            if (amount) {
                const layout = await Layout.getLoadedLayout("lme/general/multicurrency/ExchangeRateQuickInfo") as ExchangeRateQuickInfo;
                await layout.sourceCurrencyConversions?.search({ currency_for_exchange: amount });
                return layout;
            }
            return null;
        };
    }

    private setupTotalPanelTotalPayQuickInfo() {
        this.textboxTotalPay.tooltipDynamicContent = async () => {
            const amount: Currency = this._totalPayCurrency;
            if (amount) {
                const layout = await Layout.getLoadedLayout("lme/general/multicurrency/ExchangeRateQuickInfo") as ExchangeRateQuickInfo;
                await layout.sourceCurrencyConversions?.search({ currency_for_exchange: amount });
                return layout;
            }
            return null;
        };
    }

    private setupTotalPanelMarginQuickInfo() {
        this.textboxMargin.tooltipDynamicContent = async () => {
            const amount: Currency = this._marginCurrency;
            if (amount) {
                const layout = await Layout.getLoadedLayout("lme/general/multicurrency/ExchangeRateQuickInfo") as ExchangeRateQuickInfo;
                await layout.sourceCurrencyConversions?.search({ currency_for_exchange: amount });
                return layout;
            }
            return null;
        };
    }

    public get fromSave(): boolean {
        return this._fromSave;
    }

    public set fromSave(value: boolean) {
        this._fromSave = value;
    }

    /** This is an event handler for the onRowDisplay event of tableExtraPay. */
    tableExtraPayOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.getTableRow();
        this.populateExtraPayDefaults(tableRow);

        const textboxAmount = tableRow?.findComponentById("textboxAmount") as Textbox;
        textboxAmount.quickInfoOverrideKeyValue = () => {
            const tableRow = event.target as TableRow;
            const modelRow = tableRow.data as ModelRow;
            if (modelRow.get("fuel_surcharge_pay") != null) {
                return modelRow.get("id");
            }
            return null;
        };

        const textboxDeductCodeId = tableRow?.findComponentById("textboxDeductCodeId") as Textbox;
        textboxDeductCodeId.quickInfoOverrideSearchMethod = async (layout: Layout, keyValue: any) => {
            if (!StringUtil.isEmptyString(keyValue)) {
                const row = tableRow.data;
                await layout.mainDataSource.search({ search: keyValue });
                layout["textboxDriveExtraPayId"].text = keyValue; //deduct_code_id
                layout["textboxDeductDescr"].text = row.get("short_desc");
            }
        };
    }
}
